import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Plus, Pencil, Trash2, X, Calendar } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const DEFAULT_CONFIGS = {
  jenis_event: [
    { value: 'club', label: 'Event Klub' },
    { value: 'school', label: 'Event Sekolah' },
  ],
  format: [
    { value: 'league_round_robin', label: 'Liga Round Robin' },
    { value: 'tournament_elimination', label: 'Tournament Eliminasi' },
  ],
  kategori: [
    { value: 'U-8', label: 'U-8' }, { value: 'U-10', label: 'U-10' },
    { value: 'U-12', label: 'U-12' }, { value: 'U-14', label: 'U-14' },
    { value: 'U-17', label: 'U-17' }, { value: 'U-21', label: 'U-21' },
  ],
  status: [
    { value: 'draft', label: 'Draft' },
    { value: 'open_registration', label: 'Buka Registrasi' },
    { value: 'close_registration', label: 'Tutup Registrasi' },
    { value: 'running', label: 'Berjalan' },
    { value: 'finished', label: 'Selesai' },
  ],
  tipe_liga: [
    { value: 'one_match', label: 'One Match (1x Pertemuan)' },
    { value: 'home_away', label: 'Home Away (2x Pertemuan)' },
  ],
  format_pemain: [
    { value: '7v7', label: '7 vs 7' },
    { value: '11v11', label: '11 vs 11' },
  ],
};

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [masterConfig, setMasterConfig] = useState(DEFAULT_CONFIGS);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_target: 'club', // 'school' or 'club'
    format: 'league_round_robin', // 'tournament_elimination' or 'league_round_robin'
    categories: [],
    status: 'draft',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    location: '',
    league_type: 'one_match', // 'home_away' or 'one_match' (for league)
    round_robin_player_format: '11v11', // '7v7' or '11v11'
    group_count: null, // for tournament (optional)
    bracket_size: 128, // for tournament knockout
    draw_mode: 'automatic', // automatic/manual seeding
  });

  const { toast } = useToast();

  useEffect(() => {
    loadEvents();
    loadMasterConfig();
  }, []);

  const loadMasterConfig = async () => {
    try {
      const response = await apiService.admin.getAppConfig();
      if (response.data) setMasterConfig({ ...DEFAULT_CONFIGS, ...response.data });
    } catch (_) {
      // fallback to defaults
    }
  };

  const loadEvents = async () => {
    try {
      const response = await apiService.admin.getEvents();
      setEvents(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat event', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.categories.length === 0) {
        toast({ title: 'Error', description: 'Pilih minimal satu kategori', variant: 'destructive' });
        return;
      }

      const eventData = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        group_count: formData.format === 'tournament_elimination' && formData.group_count
          ? parseInt(formData.group_count)
          : null,
        bracket_size: formData.format === 'tournament_elimination' && formData.bracket_size
          ? parseInt(formData.bracket_size)
          : null,
      };

      if (editingEvent) {
        await apiService.admin.updateEvent(editingEvent.id, eventData);
        toast({ title: 'Berhasil', description: 'Event berhasil diupdate' });
      } else {
        await apiService.admin.createEvent(eventData);
        toast({ title: 'Berhasil', description: 'Event berhasil dibuat' });
      }

      loadEvents();
      closeModal();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menyimpan event', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus event ini? Semua data terkait juga akan dihapus')) return;
    try {
      await apiService.admin.deleteEvent(id);
      toast({ title: 'Berhasil', description: 'Event berhasil dihapus' });
      loadEvents();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menghapus event', variant: 'destructive' });
    }
  };

  const openModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        name: event.name,
        description: event.description || '',
        event_target: event.event_target || 'club',
        format: event.format,
        categories: event.categories || [],
        status: event.status,
        start_date: new Date(event.start_date).toISOString().split('T')[0],
        end_date: event.end_date ? new Date(event.end_date).toISOString().split('T')[0] : '',
        location: event.location,
        league_type: event.league_type || 'one_match',
        round_robin_player_format: event.round_robin_player_format || '11v11',
        group_count: event.group_count ?? null,
        bracket_size: event.bracket_size ?? 128,
        draw_mode: event.draw_mode || 'automatic',
      });
    } else {
      setEditingEvent(null);
      setFormData({
        name: '',
        description: '',
        event_target: 'club',
        format: 'league_round_robin',
        categories: [],
        status: 'draft',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        location: '',
        league_type: 'one_match',
        round_robin_player_format: '11v11',
        group_count: null,
        bracket_size: 128,
        draw_mode: 'automatic',
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEvent(null);
  };

  const toggleCategory = (category) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter((c) => c !== category)
        : [...prev.categories, category],
    }));
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      open_registration: 'Buka Registrasi',
      close_registration: 'Tutup Registrasi',
      running: 'Berjalan',
      finished: 'Selesai',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      open_registration: 'bg-blue-100 text-blue-700',
      close_registration: 'bg-yellow-100 text-yellow-700',
      running: 'bg-green-100 text-green-700',
      finished: 'bg-gray-100 text-gray-700',
    };
    return colors[status] || 'bg-gray-100';
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredEvents = events.filter((event) => {
    if (!normalizedSearch) return true;
    const haystacks = [
      event.name,
      event.description,
      event.location,
      event.event_target,
      event.format,
      event.status,
      ...(event.categories || []),
    ];
    return haystacks.some((value) => (value || '').toString().toLowerCase().includes(normalizedSearch));
  });

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold">Kelola Event</h2>
          <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari event, lokasi, kategori..."
              className="w-full sm:w-72 px-3 py-2 border rounded-lg"
            />
            <button
              onClick={() => openModal()}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center sm:justify-start space-x-2 font-medium"
            >
              <Plus className="h-5 w-5" />
              <span>Buat Event</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Memuat...</div>
        ) : filteredEvents.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 text-center">
            <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">
              {events.length === 0
                ? 'Belum ada event. Buat event pertama Anda sekarang!'
                : 'Event tidak ditemukan. Coba kata kunci lain.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {filteredEvents.map((event) => (
              <div key={event.id} className="bg-white rounded-lg shadow-md p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-0">
                  <div className="flex-1 w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900">{event.name}</h3>
                      <span className={`inline-block px-3 py-1 text-xs rounded-full font-medium w-fit ${getStatusColor(event.status)}`}>
                        {getStatusLabel(event.status)}
                      </span>
                    </div>
                    {event.description && <p className="text-sm text-gray-600 mb-2 line-clamp-2">{event.description}</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                      <div>
                        <span className="font-medium">Jenis Event:</span> {event.event_target === 'school' ? 'Sekolah' : 'Klub'}
                      </div>
                      <div>
                        <span className="font-medium">Format:</span> {event.format === 'tournament_elimination' ? 'Tournament Eliminasi' : 'Liga Round Robin'}
                        {event.format === 'league_round_robin' && event.round_robin_player_format && (
                          <span className="ml-1">({event.round_robin_player_format})</span>
                        )}
                      </div>
                      <div>
                        <span className="font-medium">Lokasi:</span> {event.location}
                      </div>
                      <div>
                        <span className="font-medium">Kategori:</span> {event.categories.join(', ')}
                      </div>
                      <div>
                        <span className="font-medium">Tanggal:</span> {new Date(event.start_date).toLocaleDateString('id-ID')} - {event.end_date ? new Date(event.end_date).toLocaleDateString('id-ID') : '-'}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {event.format === 'tournament_elimination' && (
                        <span>
                          Bracket: {event.bracket_size || 128} peserta • Drawing: {event.draw_mode === 'manual' ? 'Manual' : 'Otomatis'}
                        </span>
                      )}
                      {event.format === 'league_round_robin' && (
                        <span>Format Liga: {event.league_type === 'home_away' ? 'Home Away' : 'One Match'}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-0 sm:ml-4 w-full sm:w-auto justify-end sm:justify-start">
                    <button
                      onClick={() => openModal(event)}
                      className="text-blue-600 hover:text-blue-800 p-1.5 hover:bg-blue-50 rounded"
                      title="Edit Event"
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(event.id)}
                      className="text-red-600 hover:text-red-800 p-1.5 hover:bg-red-50 rounded"
                      title="Hapus Event"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
            <div className="bg-white rounded-lg w-full sm:max-w-2xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">{editingEvent ? 'Edit Event' : 'Buat Event Baru'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nama Event */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nama Event *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                    placeholder="e.g. Tournament U-12 2026"
                    required
                  />
                </div>

                {/* Deskripsi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deskripsi</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                    rows="3"
                    placeholder="Deskripsi event..."
                  />
                </div>

                {/* Format */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Jenis Event *</label>
                  <select
                    value={formData.event_target}
                    onChange={(e) => setFormData({ ...formData, event_target: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                    required
                  >
                    {(masterConfig.jenis_event || DEFAULT_CONFIGS.jenis_event).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Pilihan peserta di Event Teams akan otomatis difilter sesuai jenis event ini.
                  </p>
                </div>

                {/* Format */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Format *</label>
                  <select
                    value={formData.format}
                    onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                    required
                  >
                    {(masterConfig.format || DEFAULT_CONFIGS.format).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Format Pemain - always visible */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Format Pemain *</label>
                  <select
                    value={formData.round_robin_player_format}
                    onChange={(e) => setFormData({ ...formData, round_robin_player_format: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                  >
                    {(masterConfig.format_pemain || DEFAULT_CONFIGS.format_pemain).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Kategori */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kategori *</label>
                  <div className="space-y-2">
                    {(masterConfig.kategori || DEFAULT_CONFIGS.kategori).map((opt) => (
                      <label key={opt.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.categories.includes(opt.value)}
                          onChange={() => toggleCategory(opt.value)}
                          className="mr-2"
                        />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                  >
                    {(masterConfig.status || DEFAULT_CONFIGS.status).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Tanggal Mulai */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tanggal Mulai *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                    required
                  />
                </div>

                {/* Tanggal Selesai */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tanggal Selesai (Opsional)</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                  />
                </div>

                {/* Lokasi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lokasi *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 border rounded-md"
                    placeholder="e.g. Jakarta, GBK"
                    required
                  />
                </div>

                {/* Conditional Fields untuk Format */}
                {formData.format === 'league_round_robin' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tipe Liga</label>
                      <select
                        value={formData.league_type}
                        onChange={(e) => setFormData({ ...formData, league_type: e.target.value })}
                        className="w-full px-4 py-2 border rounded-md"
                      >
                        {(masterConfig.tipe_liga || DEFAULT_CONFIGS.tipe_liga).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {formData.format === 'tournament_elimination' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Jumlah Peserta Bracket</label>
                      <select
                        value={formData.bracket_size ?? 128}
                        onChange={(e) => setFormData({ ...formData, bracket_size: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border rounded-md"
                      >
                        <option value={8}>8</option>
                        <option value={16}>16</option>
                        <option value={32}>32</option>
                        <option value={64}>64</option>
                        <option value={128}>128</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Jalur knockout akan bergerak otomatis dari {formData.bracket_size || 128} sampai final.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mode Drawing / Kocok</label>
                      <select
                        value={formData.draw_mode}
                        onChange={(e) => setFormData({ ...formData, draw_mode: e.target.value })}
                        className="w-full px-4 py-2 border rounded-md"
                      >
                        <option value="automatic">Otomatis (acak)</option>
                        <option value="manual">Manual (urut sesuai pendaftaran)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Jumlah Grup (Opsional)</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.group_count ?? ''}
                        onChange={(e) => setFormData({ ...formData, group_count: e.target.value === '' ? null : parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border rounded-md"
                        placeholder="Isi jika tetap ingin pakai fase grup"
                      />
                    </div>
                  </>
                )}

                {/* Buttons */}
                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
                  >
                    {editingEvent ? 'Update' : 'Buat'} Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEvents;
